// Text-to-Speech utility for accessibility
// Uses browser's built-in Web Speech API

class TTSManager {
  constructor() {
    this.synth = window.speechSynthesis;
    this.speaking = false;
    this.enabled = false;
    this.autoRead = false;
    this.rate = 0.9; // Slightly slower for clarity
    this.voice = null;
    this.currentLang = 'en';
    
    // Language to voice mapping
    this.langVoiceMap = {
      en: ['en-US', 'en-GB', 'en'],
      es: ['es-ES', 'es-MX', 'es-US', 'es'],
      fr: ['fr-FR', 'fr-CA', 'fr'],
      pl: ['pl-PL', 'pl']
    };
  }

  // Initialize and find best voice for language
  init(lang = 'en') {
    this.currentLang = lang;
    this.loadVoice();
    
    // Voices may load async
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => this.loadVoice();
    }
  }

  loadVoice() {
    const voices = this.synth.getVoices();
    const langPrefixes = this.langVoiceMap[this.currentLang] || ['en'];
    
    // Find best matching voice
    for (const prefix of langPrefixes) {
      const voice = voices.find(v => v.lang.startsWith(prefix));
      if (voice) {
        this.voice = voice;
        return;
      }
    }
    
    // Fallback to first available
    if (voices.length > 0) {
      this.voice = voices[0];
    }
  }

  setLanguage(lang) {
    this.currentLang = lang;
    this.loadVoice();
  }

  speak(text, onEnd = null) {
    if (!this.synth || !text) return;
    
    // Cancel any current speech
    this.stop();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = this.rate;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    if (this.voice) {
      utterance.voice = this.voice;
    }
    
    utterance.onstart = () => {
      this.speaking = true;
    };
    
    utterance.onend = () => {
      this.speaking = false;
      if (onEnd) onEnd();
    };
    
    utterance.onerror = () => {
      this.speaking = false;
    };
    
    this.synth.speak(utterance);
  }

  stop() {
    if (this.synth) {
      this.synth.cancel();
      this.speaking = false;
    }
  }

  toggle() {
    if (this.speaking) {
      this.stop();
    }
  }

  isSupported() {
    return 'speechSynthesis' in window;
  }
}

// Singleton instance
const ttsManager = new TTSManager();

export default ttsManager;

// Helper function to extract readable text from form step
export const getStepReadableText = (stepId, t) => {
  const stepTexts = {
    personal: `${t('personalInfo')}. ${t('firstName')}, ${t('lastName')}, ${t('middleInitial')}, ${t('dateOfBirth')}, ${t('ssn')}, ${t('gender')}.`,
    contact: `${t('contactAddress')}. ${t('streetAddress')}, ${t('city')}, ${t('state')}, ${t('zipCode')}, ${t('phoneNumber')}, ${t('emailAddress')}.`,
    emergency: `${t('emergencyContact')}. ${t('emergencyContactDesc')} ${t('contactName')}, ${t('contactPhone')}.`,
    demographics: `${t('demographics')}. ${t('hispanicLatino')} ${t('race')} ${t('relationshipStatus')}.`,
    living: `${t('livingSituation')}. ${t('selectLivingSituation')}`,
    needs: `${t('yourNeeds')}. ${t('howDidYouHear')} ${t('whatDoYouNeed')}`,
    household: `${t('household')}. ${t('tellUsAboutHousehold')} ${t('householdSize')}, ${t('numberOfAdults')}, ${t('numberOfChildren')}.`,
    income: `${t('monthlyIncomeTitle')}. ${t('incomeDescription')}`,
    consent: `${t('consent')}. ${t('informedConsent')}. ${t('iAgree')}.`
  };
  
  return stepTexts[stepId] || '';
};
